import { useMemo, Fragment, type FC } from "react"
import { useTranslation } from "react-i18next"
import Stack from "@mui/material/Stack"
import Typography from "@mui/material/Typography"
import Tooltip from "@mui/material/Tooltip"
import Box from "@mui/material/Box"
import Divider from "@mui/material/Divider"
import { DateTime } from "luxon"
import fp from "lodash/fp"

import type { MultiValueStatPlotView, RecordType } from "types/dashboard.types"
import { valueFormatter } from "helpers/formatters/plots/plots"

interface MultiValueStatProps {
  view: MultiValueStatPlotView
  data: RecordType[]
}

const MultiValueStat: FC<MultiValueStatProps> = ({ view, data }) => {
  const { t, i18n } = useTranslation()
  const locale = i18n.resolvedLanguage ?? "en"
  const { rows, suffix = "", decimalPlaces = 2 } = view.properties

  const dataByField = useMemo(
    () => fp.keyBy("_field", data) as Record<string, RecordType>,
    [data],
  )

  const visibleRows = useMemo(
    () => rows.filter((row) => dataByField[row.field] != null),
    [rows, dataByField],
  )

  const showLabels = visibleRows.length > 1

  if (visibleRows.length === 0) {
    return (
      <Stack alignItems="center" justifyContent="center" height="100%">
        <Typography variant="body2" color="text.secondary">
          No data
        </Typography>
      </Stack>
    )
  }

  return (
    <Stack justifyContent="space-evenly" height="100%" px={0} py={0} gap={0}>
      {visibleRows.map((row, index) => {
        const record = dataByField[row.field]
        const value = record._value
        const time = record._time as string | undefined
        const formatted =
          typeof value === "number"
            ? valueFormatter({ value, suffix, decimalPlaces, locale, formatUnits: false })
            : "—"
        const formattedTime = time
          ? DateTime.fromISO(time).toFormat("yyyy-MM-dd HH:mm")
          : undefined

        return (
          <Fragment key={row.field}>
            {index > 0 && <Divider />}
            <Tooltip title={showLabels ? t(row.labelKey) : ""} arrow>
              <Stack alignItems="center" justifyContent="center" px={1} py={0.25} gap={0}>
                <Stack
                  direction="row"
                  alignItems="center"
                  justifyContent={showLabels ? "space-between" : "center"}
                  width="100%"
                  gap={0.75}
                >
                  {showLabels && (
                    <Box
                      sx={{
                        width: 8,
                        height: 8,
                        borderRadius: "50%",
                        bgcolor: row.color,
                        flexShrink: 0,
                      }}
                    />
                  )}
                  <Typography
                    fontWeight="700"
                    fontSize={showLabels ? "0.95rem" : "1.5rem"}
                    noWrap
                    sx={{ flexShrink: 0 }}
                  >
                    {formatted}
                  </Typography>
                  {showLabels && formattedTime && (
                    <Typography
                      variant="caption"
                      color="text.secondary"
                      noWrap
                      sx={{ ml: "auto" }}
                    >
                      {formattedTime}
                    </Typography>
                  )}
                </Stack>
                {!showLabels && formattedTime && (
                  <Typography
                    variant="caption"
                    color="text.secondary"
                    noWrap
                    textAlign="center"
                  >
                    {formattedTime}
                  </Typography>
                )}
              </Stack>
            </Tooltip>
          </Fragment>
        )
      })}
    </Stack>
  )
}

export default MultiValueStat
